import {
  DashIcon,
  StepProgressBar,
  SyncIcon,
  Typography,
  useTheme,
} from "@codat/orchard-ui";
import { SourceTypeResponse, SourceTypesQueryResponse } from "api/company";
import classNames from "classnames";
import { useRouteMatch } from "react-router";
import { sourceTypePlatformName } from "utils";

import { routes } from "config/urls";

import { LinkedAccountIcon } from "./LinkedAccountIcon/LinkedAccountIcon";
import styles from "./LinkedAccounts.module.css";
import { RouteMatchResponse, useCurrentStep } from "./useCurrentStep";

interface LinkedAccountItemProps {
  sourceType: SourceTypeResponse;
  currentSourceType?: SourceTypeResponse;
  sourceTypes: SourceTypesQueryResponse;
  bankingOnlyFileUpload: boolean;
}

const LinkedAccountItem: React.FC<LinkedAccountItemProps> = ({
  sourceType,
  currentSourceType,
  sourceTypes,
  bankingOnlyFileUpload,
}) => {
  const { currentStep, textDisabled, sourceTypeSkipped } = useCurrentStep(
    sourceType,
    currentSourceType,
    sourceTypes
  );
  const text = (() => {
    if (sourceTypeSkipped) {
      return (
        <>
          {sourceType.sourceType}
          <span className={styles.textBold}> skipped</span>
        </>
      );
    }
    if (sourceType.sourceType === "Banking" && bankingOnlyFileUpload) {
      return <>Upload your bank documents</>;
    }
    if (sourceType.sourceType === "Other") {
      return <>Upload your business documents</>;
    }
    return <>Connect your {sourceTypePlatformName[sourceType.sourceType]}</>;
  })();

  return (
    <div
      className={classNames(styles.container, {
        [styles.containerWithProgressBar]: currentStep > 0,
        [styles.disabled]: textDisabled,
      })}
    >
      <div className={styles.linkInfo}>
        <LinkedAccountIcon
          className={styles.inheritColor}
          sourceType={sourceType}
          currentSourceType={currentSourceType?.sourceType}
        />
        <Typography
          variant="p"
          as="p"
          className={classNames(styles.text, styles.inheritColor, {
            [styles.textBold]: currentStep > 0,
          })}
          data-testid={`linked-account-${sourceType.sourceType.toLowerCase()}`}
        >
          {text}
        </Typography>
      </div>
      {currentStep > 0 && (
        <div className={styles.progressBar}>
          <StepProgressBar
            steps={4}
            currentStep={currentStep}
            color="var(--display-color)"
            data-testid={`progress-bar-${sourceType.sourceType.toLowerCase()}`}
          />
        </div>
      )}
    </div>
  );
};

interface LinkedAccountsProps {
  sourceTypes: SourceTypesQueryResponse;
  currentSourceType?: SourceTypeResponse;
  bankingOnlyFileUpload: boolean;
}

export const LinkedAccounts = ({
  sourceTypes,
  currentSourceType,
  bankingOnlyFileUpload,
}: LinkedAccountsProps) => {
  const { colors } = useTheme();

  const overview: RouteMatchResponse | null = useRouteMatch(
    routes.company.overview
  );
  return (
    <section>
      {sourceTypes.map((sourceType) => (
        <LinkedAccountItem
          sourceType={sourceType}
          key={sourceType.sourceType}
          currentSourceType={currentSourceType}
          sourceTypes={sourceTypes}
          bankingOnlyFileUpload={bankingOnlyFileUpload}
        />
      ))}
      <div className={styles.container}>
        <div className={styles.linkInfo}>
          <span
            data-testid="linked-account-icon"
            className={classNames(styles.icon, {
              [styles.current]: overview,
            })}
          >
            {overview ? (
              <SyncIcon fillColor={colors["color-ui-action"]} />
            ) : (
              <DashIcon fillColor={colors["color-ui-interface"]} />
            )}
          </span>
          <Typography
            variant="p"
            as="p"
            className={classNames(styles.text, { [styles.textBold]: overview })}
            style={{
              color: overview
                ? colors["color-ui-action"]
                : colors["color-ui-interface"],
            }}
          >
            Overview
          </Typography>
        </div>
      </div>
    </section>
  );
};
